import { useMemo, useState } from 'react'
import { TableShell } from '../../components/molecules/TableShell'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Select } from '../../components/atoms/Select'
import { Spinner } from '../../components/atoms/Spinner'
import { Th, Td } from '../../components/molecules/Table'
import { Paginator } from '../../components/molecules/Paginator'
import { ProductFormModal } from './ProductFormModal'
import { useProducts, type ProductDto } from '../../../viewmodels/useProducts'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'
import type { CategoryTreeNode } from '../../../models/product.model'

/**
 * /products/list — list + create/edit/delete/decommission.
 *
 * Each product is bound to ONE category. The form's dynamic per-
 * attribute inputs are driven by the categoryTree shipped alongside
 * the list response (see useProducts.ts).
 *
 * 2026-05-19: Delete coexists with Decommission. Delete is allowed
 * only when active_variant_count === 0 (the product hasn't been
 * propagated); propagated products must be decommissioned instead.
 */
export function ProductsPage(): JSX.Element {
  // Category filter lives on the page (not the viewmodel) so the dropdown
  // + a future "Showing X of Y in <category>" caption can read it without
  // re-wiring. `null` = no filter.
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null)
  const {
    items,
    categoryTree,
    total,
    page,
    pageSize,
    isLoading,
    error,
    setPage,
    reload,
    create,
    update,
    decommission,
    remove,
    propagateVariants
  } = useProducts(categoryFilter)
  const { can } = useAuth()
  const [modalMode, setModalMode] = useState<'closed' | 'create' | 'edit'>('closed')
  const [editTargetId, setEditTargetId] = useState<string | null>(null)
  // editTarget is derived from items so any list-row update (e.g. a
  // successful Propagate) flows straight into the open modal — the
  // modal's `initial.propagated_axes_selections` becomes the new
  // baseline immediately, and the Propagate button flips back to
  // "in sync" without a modal close + re-open.
  const editTarget = useMemo<ProductDto | null>(
    () => (editTargetId === null ? null : items.find((it) => it.id === editTargetId) ?? null),
    [editTargetId, items]
  )
  const [decommissioningId, setDecommissioningId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  const openCreate = (): void => {
    setEditTargetId(null)
    setModalMode('create')
  }
  const openEdit = (row: ProductDto): void => {
    setEditTargetId(row.id)
    setModalMode('edit')
  }
  const closeModal = (): void => setModalMode('closed')

  const handleDecommission = async (row: ProductDto): Promise<void> => {
    if (
      typeof window !== 'undefined' &&
      !window.confirm(`Decommission "${row.name}"? It will stay in the catalogue but be flagged as no longer selling.`)
    ) return
    setDecommissioningId(row.id)
    setRowError(null)
    const res = await decommission(row.id)
    setDecommissioningId(null)
    if (!res.ok) setRowError(res.message)
  }

  const handleDelete = async (row: ProductDto): Promise<void> => {
    if (
      typeof window !== 'undefined' &&
      !window.confirm(`Delete "${row.name}"? This removes the product from the catalogue. Existing variants/media stay in audit history.`)
    ) return
    setDeletingId(row.id)
    setRowError(null)
    const res = await remove(row.id)
    setDeletingId(null)
    if (!res.ok) setRowError(res.message)
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <h1 className="font-heading text-h2 text-charcoal">Products</h1>
        {can(SCREENCODES.PRODUCTS, 'add') ? <Button onClick={openCreate}>Add</Button> : null}
      </header>

      <CategoryFilter
        categoryTree={categoryTree}
        value={categoryFilter}
        onChange={setCategoryFilter}
      />

      {rowError !== null ? <Alert tone="error">{rowError}</Alert> : null}
      {error !== null ? (
        <Alert tone="error">
          {error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={() => void reload()}
          >
            Retry
          </button>
        </Alert>
      ) : null}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          filtered={categoryFilter !== null}
          onClearFilter={() => setCategoryFilter(null)}
        />
      ) : (
        <>
          <Table
            items={items}
            onEdit={openEdit}
            onDecommission={(row) => void handleDecommission(row)}
            onDelete={(row) => void handleDelete(row)}
            decommissioningId={decommissioningId}
            deletingId={deletingId}
          />
          <Paginator
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            disabled={isLoading}
          />
        </>
      )}

      <ProductFormModal
        open={modalMode !== 'closed'}
        mode={modalMode === 'edit' ? 'edit' : 'create'}
        initial={editTarget}
        categoryTree={categoryTree}
        onClose={closeModal}
        onSubmit={async (input) => {
          if (modalMode === 'edit' && editTarget !== null) {
            return update(editTarget.id, input)
          }
          return create(input as Parameters<typeof create>[0])
        }}
        onPropagate={propagateVariants}
      />
    </div>
  )
}

/**
 * Compact filter strip above the products table. Sets the established
 * pattern for "filter dropdown above a paginated table" on this app —
 * inline label in muted text + the standard Select atom (SOP 13
 * styling) + a subtle Clear link that appears only when a filter is
 * active. The strip itself has no card chrome so it reads as toolbar,
 * not content.
 *
 * Categories are sorted alphabetically; "All categories" sits at the
 * top as the unfiltered default. The tree is the same source the form
 * modal uses — no extra fetch.
 */
function CategoryFilter({
  categoryTree,
  value,
  onChange
}: {
  readonly categoryTree: ReadonlyArray<CategoryTreeNode>
  readonly value: string | null
  readonly onChange: (next: string | null) => void
}): JSX.Element {
  const sorted = useMemo(
    () => [...categoryTree].sort((a, b) => a.name.localeCompare(b.name)),
    [categoryTree]
  )
  return (
    <div className="flex flex-wrap items-center gap-3">
      <label className="flex items-center gap-2 font-body text-body-sm text-text-secondary">
        <span>Category</span>
        <Select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
          className="min-w-[220px]"
          aria-label="Filter by category"
        >
          <option value="">All categories</option>
          {sorted.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
      </label>
      {value !== null ? (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="font-body text-body-sm text-text-secondary underline-offset-2 hover:text-forest hover:underline"
        >
          Clear
        </button>
      ) : null}
    </div>
  )
}

function EmptyState({
  filtered,
  onClearFilter
}: {
  readonly filtered: boolean
  readonly onClearFilter: () => void
}): JSX.Element {
  if (filtered) {
    return (
      <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
        <p className="font-heading text-h3 text-charcoal">No products in this category</p>
        <p className="font-body text-body-sm text-text-secondary mt-2">
          Pick a different category or{' '}
          <button
            type="button"
            onClick={onClearFilter}
            className="font-medium text-forest underline-offset-2 hover:underline"
          >
            clear the filter
          </button>
          .
        </p>
      </div>
    )
  }
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-heading text-h3 text-charcoal">No products yet</p>
      <p className="font-body text-body-sm text-text-secondary mt-2">
        Use <span className="font-medium">Add</span> at the top right to create
        the first product.
      </p>
    </div>
  )
}

function Table({
  items,
  onEdit,
  onDecommission,
  onDelete,
  decommissioningId,
  deletingId
}: {
  readonly items: ProductDto[]
  readonly onEdit: (row: ProductDto) => void
  readonly onDecommission: (row: ProductDto) => void
  readonly onDelete: (row: ProductDto) => void
  readonly decommissioningId: string | null
  readonly deletingId: string | null
}): JSX.Element {
  const { can } = useAuth()
  const canEdit = can(SCREENCODES.PRODUCTS, 'edit')
  const canDelete = can(SCREENCODES.PRODUCTS, 'delete')
  return (
    <TableShell>
      <table className="w-full font-body text-body-sm">
        <thead className="bg-parchment/50 text-text-secondary">
          <tr>
            <Th className="w-16">Image</Th>
            <Th>Name</Th>
            <Th>Category</Th>
            <Th>Description</Th>
            <Th>Attributes</Th>
            <Th>Media</Th>
            <Th>Components</Th>
            <Th>Status</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => {
            const filled =
              row.attribute_values.length + row.attribute_custom_values.length
            return (
              <tr
                key={row.id}
                className={`border-t border-border ${row.is_decommissioned ? 'opacity-60' : ''}`}
              >
                <Td>
                  {row.thumbnail_url !== null ? (
                    <img
                      src={row.thumbnail_url}
                      alt={row.name}
                      className="w-12 h-12 rounded object-cover bg-parchment/40"
                      crossOrigin="use-credentials"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded bg-parchment/40 flex items-center justify-center font-mono text-body-xs text-text-secondary">
                      —
                    </div>
                  )}
                </Td>
                <Td>{row.name}</Td>
                <Td>
                  <span className="font-mono text-text-secondary">{row.category_name}</span>
                </Td>
                <Td
                  className="max-w-[24rem] truncate"
                  title={row.description}
                >
                  {row.description}
                </Td>
                <Td>{filled}</Td>
                <Td>{row.media.length}</Td>
                <Td>
                  {row.active_component_count > 0 ? (
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full bg-honey/20 text-charcoal font-medium text-body-xs"
                      title={`Bundle of ${row.active_component_count} component${row.active_component_count === 1 ? '' : 's'}`}
                    >
                      {row.active_component_count}
                    </span>
                  ) : (
                    <span className="font-mono text-text-secondary">—</span>
                  )}
                </Td>
                <Td>
                  {row.is_decommissioned ? (
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full bg-terra/15 text-terra font-medium text-body-xs"
                      title={
                        row.decommissioned_at !== null
                          ? `Decommissioned ${row.decommissioned_at.slice(0, 10)}`
                          : 'Decommissioned'
                      }
                    >
                      Decommissioned
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-forest/15 text-forest font-medium text-body-xs">
                      Active
                    </span>
                  )}
                </Td>
                <Td className="text-right whitespace-nowrap">
                  {canEdit ? (
                    <>
                      <Button size="sm" variant="secondary" onClick={() => onEdit(row)}>
                        Edit
                      </Button>{' '}
                    </>
                  ) : null}
                  {canDelete ? (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onDecommission(row)}
                        disabled={decommissioningId === row.id || row.is_decommissioned}
                        title={row.is_decommissioned ? 'Already decommissioned' : ''}
                      >
                        {decommissioningId === row.id ? 'Decommissioning…' : 'Decommission'}
                      </Button>{' '}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onDelete(row)}
                        disabled={deletingId === row.id || row.active_variant_count > 0}
                        title={
                          row.active_variant_count > 0
                            ? `${row.active_variant_count} variant${row.active_variant_count === 1 ? '' : 's'} — decommission this product instead.`
                            : ''
                        }
                      >
                        {deletingId === row.id ? 'Deleting…' : 'Delete'}
                      </Button>
                    </>
                  ) : null}
                </Td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </TableShell>
  )
}
